import { Building2, Search, UserRound, X } from 'lucide-react';
import { type ReactNode, useEffect, useState } from 'react';
import type {
  BookerCandidateAffiliate,
  BookerCandidateCustomer,
  BookerSuggestions,
} from '../../../api/comms';
import { Spinner } from '../../../components/Spinner';
import { useLoad } from '../../../lib/useLoad';
import { InfoTip } from '../InfoTip';
import type { BookerPick } from './bookingForm';
import { defaultPickerTab, effectiveQuery, pickerEmpty, pickerLead, reasonLabel } from './bookerCandidates';

const SEARCH_DEBOUNCE_MS = 300;

/**
 * Find the dispatch account behind a contact — for the booking box when the
 * draft found nobody (or the wrong somebody), and for the contact panel's
 * link control. Both sides, in both places: an address is a person's or a
 * booking desk's, and which one is what the agent is here to say.
 *
 * It opens on SUGGESTIONS (the same name, colleagues the CRM already linked,
 * the same email domain) and searches as the agent types. Nothing is picked
 * for them: a shared domain says where somebody works, not who they are, and
 * the first new hire at a company would otherwise be booked and billed as
 * their colleague.
 *
 * "Couldn't check" and "nobody" are different lines, never the same empty
 * list: the first sends the agent to dispatch, the second to a guest booking.
 */
export function BookerPicker({
  contactId,
  suggest,
  initial = null,
  initialTab = null,
  title,
  titleTip = null,
  busy = false,
  error = null,
  footer = null,
  onPick,
  onClose,
}: {
  contactId: number;
  suggest: (contactId: number, q?: string) => Promise<BookerSuggestions>;
  /** Suggestions already in hand (the booking draft sends them) — skips the first read. */
  initial?: BookerSuggestions | null;
  /** The tab to open on; null picks whichever side the suggestions found. */
  initialTab?: 'customers' | 'affiliates' | null;
  title: string;
  /** What picking here does, behind a "?" beside the title. */
  titleTip?: string | null;
  /** A pick is being saved: rows stay visible but cannot be pressed twice. */
  busy?: boolean;
  error?: string | null;
  footer?: ReactNode;
  onPick: (pick: BookerPick) => void;
  onClose: () => void;
}) {
  const [typed, setTyped] = useState('');
  const [query, setQuery] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => setQuery(effectiveQuery(typed)), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [typed]);

  const load = useLoad(
    () => (!query && initial ? Promise.resolve(initial) : suggest(contactId, query || undefined)),
    [contactId, query],
  );
  const result = load.stale ? null : load.data;
  const [chosenTab, setChosenTab] = useState<'customers' | 'affiliates' | null>(initialTab);
  const tab = chosenTab ?? defaultPickerTab(result);
  const rows: (BookerCandidateCustomer | BookerCandidateAffiliate)[] = result
    ? tab === 'customers'
      ? result.customers
      : result.affiliates
    : [];
  const unavailable = result ? result.unavailable[tab] : null;
  const domain = result?.domain ?? null;
  const lead = pickerLead(result, query);

  return (
    <div className="bkp" role="dialog" aria-label={title}>
      <div className="bkp-head">
        <span className="bkp-title">
          {title}
          {titleTip && <InfoTip text={titleTip} label={`About ${title}`} />}
        </span>
        <button type="button" className="bkp-close" onClick={onClose} aria-label="Close" title="Close">
          <X size={13} />
        </button>
      </div>
      <label className="bkp-search">
        <Search size={13} aria-hidden />
        <input
          className="aib-input"
          // The picker opens because the agent asked for it; typing should start there.
          autoFocus
          placeholder="Search by name, email or phone"
          aria-label="Search dispatch"
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
        />
      </label>
      <div className="bkp-tabs" role="tablist">
        {(['customers', 'affiliates'] as const).map((key) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={tab === key}
            className={`bkp-tab${tab === key ? ' active' : ''}`}
            onClick={() => setChosenTab(key)}
          >
            {key === 'customers' ? 'Customers' : 'Affiliates'}
            {result ? ` (${result[key].length})` : ''}
          </button>
        ))}
      </div>
      <div className="bkp-lead">
        {lead.label}
        {lead.tip && <InfoTip text={lead.tip} label="Why these are suggested" />}
      </div>
      {load.error && !result ? (
        <div className="bkp-note warn">Couldn’t check dispatch — {load.error.message}</div>
      ) : load.loading && !result ? (
        <div className="bkp-busy">
          <Spinner /> Asking dispatch…
        </div>
      ) : unavailable ? (
        <div className="bkp-note warn">Couldn’t check dispatch — {unavailable}</div>
      ) : rows.length ? (
        <ul className="bkp-list">
          {tab === 'customers'
            ? (rows as BookerCandidateCustomer[]).map((customer) => (
                <li key={`c${customer.id}`}>
                  <button
                    type="button"
                    className="bkp-row"
                    disabled={busy}
                    onClick={() => onPick({ type: 'customer', customer })}
                  >
                    <span className="bkp-row-main">
                      <UserRound size={12} aria-hidden />
                      <strong>{customer.name || `Customer #${customer.id}`}</strong>
                      {customer.customerType && <span className="bkp-type">{customer.customerType.label}</span>}
                      {customer.businessName && <span className="bkp-biz">· {customer.businessName}</span>}
                    </span>
                    <span className="bkp-row-sub">
                      {[customer.email, customer.alternateEmail, customer.phone, `#${customer.id}`]
                        .filter(Boolean)
                        .join(' · ')}
                    </span>
                    <Reasons reasons={customer.matchedOn.map((r) => reasonLabel(r, domain, !!query))} />
                  </button>
                </li>
              ))
            : (rows as BookerCandidateAffiliate[]).map((affiliate) => (
                <li key={`a${affiliate.id}`}>
                  <button
                    type="button"
                    className="bkp-row"
                    disabled={busy}
                    onClick={() => onPick({ type: 'affiliate', affiliate })}
                  >
                    <span className="bkp-row-main">
                      <Building2 size={12} aria-hidden />
                      <strong>{affiliate.companyName || `Affiliate #${affiliate.id}`}</strong>
                      <span className="bkp-type">Affiliate</span>
                      {affiliate.primaryContactName && (
                        <span className="bkp-biz">· {affiliate.primaryContactName}</span>
                      )}
                    </span>
                    <span className="bkp-row-sub">
                      {[affiliate.matchedEmail ?? affiliate.email, affiliate.phone, `#${affiliate.id}`]
                        .filter(Boolean)
                        .join(' · ')}
                    </span>
                    <Reasons reasons={affiliate.matchedOn.map((r) => reasonLabel(r, domain, !!query))} />
                  </button>
                </li>
              ))}
        </ul>
      ) : (
        <div className="bkp-empty">{pickerEmpty(query)}</div>
      )}
      {error && <div className="bkp-note err">{error}</div>}
      {footer}
    </div>
  );
}

function Reasons({ reasons }: { reasons: string[] }) {
  if (!reasons.length) return null;
  return (
    <span className="bkp-reasons">
      {reasons.map((reason) => (
        <span key={reason} className="bkp-reason">
          {reason}
        </span>
      ))}
    </span>
  );
}
